import { useState, useRef, useEffect } from "react";
import HeaderBar from "../components/headerBar";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Plus,
  Share2,
  Calendar as CalendarIcon,
  Search,
  LayoutGrid,
  List
} from "lucide-react";
import Button from "../../../components/common/Button";

const Events = () => {
  const calendarRef = useRef(null);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState(null);
  const [currentTitle, setCurrentTitle] = useState(new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date()));
  const [isCalendarMenuOpen, setIsCalendarMenuOpen] = useState(false);

  const formatToUTCBasic = (dateInput) => {
    try {
      const date = new Date(dateInput);
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    } catch (e) {
      return "";
    }
  };



  const handleDownloadSingleEventICS = (eventObj) => {
    const icsLines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Codehub Technologies//CoreDesign//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${eventObj.id}@codehubtech.com`,
      `DTSTAMP:${formatToUTCBasic(new Date())}`,
      `DTSTART:${formatToUTCBasic(eventObj.start)}`,
      `DTEND:${formatToUTCBasic(eventObj.end)}`,
      `SUMMARY:${eventObj.title}`,
      `DESCRIPTION:${eventObj.description.replace(/\n/g, "\\n")}`,
      `LOCATION:${eventObj.location}`,
      "END:VEVENT",
      "END:VCALENDAR"
    ];
    const icsContent = icsLines.join("\r\n");

    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const sanitizedTitle = eventObj.title.toLowerCase().replace(/[^a-z0-9]/g, "_");
    link.setAttribute("download", `${sanitizedTitle}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrev = () => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.prev();
    setCurrentTitle(calendarApi.view.title);
  };

  const handleNext = () => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.next();
    setCurrentTitle(calendarApi.view.title);
  };

  const handleToday = () => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.today();
    setCurrentTitle(calendarApi.view.title);
  };

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch events dynamically from the Django EventManagement backend API
  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const token = localStorage.getItem("access");
      const headers = token ? { Authorization: `JWT ${token}` } : {};

      const response = await axios.get(`${baseUrl}/events/`, { headers });
      if (response.data) {
        // Map backend REST fields to frontend timeline formats
        const mappedEvents = response.data.map(event => {
          const startDateObj = new Date(event.start_date);
          const endDateObj = new Date(event.end_date);

          const timeStr = event.start_date && event.end_date
            ? `${startDateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })} - ${endDateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`
            : "All Day";

          const today = new Date();
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);

          let dateLabel = startDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          if (startDateObj.toDateString() === today.toDateString()) {
            dateLabel = "Today";
          } else if (startDateObj.toDateString() === tomorrow.toDateString()) {
            dateLabel = "Tomorrow";
          }

          let statusLabel = "Upcoming";
          const diffMs = startDateObj.getTime() - today.getTime();
          if (startDateObj < today && endDateObj > today) {
            statusLabel = "Live Now";
          } else if (diffMs > 0 && diffMs < 3600000) {
            statusLabel = `Starts in ${Math.round(diffMs / 60000)}m`;
          } else if (event.event_type === 'deadline') {
            statusLabel = "Due Soon";
          }

          return {
            id: event.id,
            title: event.title,
            description: event.description || "No description provided.",
            start: event.start_date,
            end: event.end_date,
            date: dateLabel,
            time: timeStr,
            location: event.location,
            color: event.color || "#3e57da",
            colorClass: event.color_class || "cht-bg-[#3e57da]",
            status: event.is_registered ? "RSVP confirmed" : statusLabel,
            type: event.event_type,
            meetingLink: event.meeting_link || "",
            host: event.instructor_detail?.name || "Academics Board",
            duration: event.meeting_link ? "1.5 hours" : "End of Day",
            isRegistered: event.is_registered || false
          };
        });
        setEvents(mappedEvents);
      }
    } catch (error) {
      console.error("Error loading events:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  // API driven RSVP toggle action
  const handleRsvpToggle = async (eventObj) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const token = localStorage.getItem("access");
      if (!token) {
        alert("Please log in to RSVP for events.");
        return;
      }
      const headers = { Authorization: `JWT ${token}` };

      const response = await axios.post(`${baseUrl}/events/${eventObj.id}/rsvp/`, {}, { headers });
      if (response.status === 200 || response.status === 201) {
        const nextReg = response.data.is_registered;

        // Update local list state
        setEvents(prev => prev.map(e => {
          if (e.id === eventObj.id) {
            return {
              ...e,
              isRegistered: nextReg,
              status: nextReg ? "RSVP confirmed" : "Upcoming"
            };
          }
          return e;
        }));

        // Update open details modal state if matching
        if (selectedEvent && selectedEvent.id === eventObj.id) {
          setSelectedEvent(prev => ({
            ...prev,
            isRegistered: nextReg,
            status: nextReg ? "RSVP confirmed" : "Upcoming"
          }));
        }
      }
    } catch (error) {
      console.error("Error toggling RSVP:", error);
      alert("Failed to update RSVP. Please make sure you are logged in.");
    }
  };

  // Synchronized search filtering
  const filteredEvents = events.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCalendarEventClick = (info) => {
    const eventId = parseInt(info.event.id);
    const clickedEvent = events.find(e => e.id === eventId);
    if (clickedEvent) {
      setSelectedEvent(clickedEvent);
    }
  };

  const handleCalendarDateClick = (info) => {
    const clickedDateStr = info.dateStr; // e.g. "2026-05-27"
    const dayEvents = events.filter(e => {
      if (!e.start) return false;
      const eventDateStr = typeof e.start === 'string'
        ? e.start.substring(0, 10)
        : new Date(e.start).toISOString().substring(0, 10);
      return eventDateStr === clickedDateStr;
    });
    setSelectedDateEvents({
      dateStr: clickedDateStr,
      list: dayEvents
    });
  };

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="Events & Schedule" />

      <main className="cht-container cht-mx-auto cht-pb-20">
        <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-8">

          {/* Left Column: Calendar or List */}
          <div className="lg:cht-col-span-8 cht-space-y-6">

            {/* Calendar Controls */}
            <div className="cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-50 cht-shadow-sm cht-flex cht-items-center cht-justify-between">
              <div className="cht-flex cht-items-center cht-gap-4">
                {viewMode === 'grid' && (
                  <div className="cht-flex cht-items-center cht-gap-2">
                    <div className="cht-flex cht-gap-1">
                      <button
                        onClick={handlePrev}
                        className="cht-p-2 cht-bg-gray-50 cht-rounded-lg hover:cht-bg-gray-100 transition-all"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        onClick={handleNext}
                        className="cht-p-2 cht-bg-gray-50 cht-rounded-lg hover:cht-bg-gray-100 transition-all"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                    <Button
                      variant="tab"
                      size="sm"
                      onClick={handleToday}
                    >
                      Today
                    </Button>
                  </div>
                )}
                <h3 className="cht-text-sm cht-font-black cht-text-black cht-uppercase cht-tracking-widest">
                  {viewMode === 'grid' ? currentTitle : `${filteredEvents.length} Events Scheduled`}
                </h3>
              </div>

              <div className="cht-flex cht-items-center cht-gap-2">
                <div className="cht-flex cht-bg-gray-50 cht-p-1 cht-rounded-xl">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`cht-p-2 cht-rounded-lg transition-all ${viewMode === 'grid' ? 'cht-bg-white cht-shadow-sm cht-text-[#3e57da]' : 'cht-text-gray-400'}`}
                  >
                    <LayoutGrid size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`cht-p-2 cht-rounded-lg transition-all ${viewMode === 'list' ? 'cht-bg-white cht-shadow-sm cht-text-[#3e57da]' : 'cht-text-gray-400'}`}
                  >
                    <List size={16} />
                  </button>
                </div>
                {/* <Button 
                  variant="primary" 
                  size="sm" 
                  leftIcon={<Plus size={14} />}
                  onClick={() => alert("Admin process: Event creation form modal")}
                >
                  Add Event
                </Button> */}
              </div>
            </div>

            {/* Calendar Grid View */}
            {viewMode === 'grid' ? (
              <div className="cht-bg-white cht-rounded-[32px] cht-p-8 cht-shadow-[0_20px_60px_rgba(0,0,0,0.03)] cht-border cht-border-gray-50">
                <div className="cht-overflow-hidden cht-rounded-2xl cht-calendar-container">
                  <style>{`
                    .fc { font-family: 'Raleway', sans-serif; border: none; }
                    .fc-theme-standard td, .fc-theme-standard th { border: 1px solid #f8fafc; }
                    .fc-theme-standard .fc-scrollgrid { border: none; }
                    .fc .fc-col-header-cell { padding: 16px 0; background: #f8fafc; border: none !important; }
                    .fc .fc-col-header-cell-cushion { font-size: 10px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.1em; color: #94a3b8; }
                    .fc .fc-daygrid-day-number { font-size: 11px; font-weight: 900; padding: 12px; color: #1e293b; }
                    .fc .fc-day-today { background: #eff6ff !important; }
                    .fc .fc-event { border: none; border-radius: 6px; padding: 4px 8px; font-size: 10px; font-weight: 800; cursor: pointer; }
                    .fc .fc-event:hover { opacity: 0.8; }
                  `}</style>
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={false}
                    selectable={true}
                    events={filteredEvents.map(e => ({
                      id: e.id.toString(),
                      title: e.title,
                      start: e.start,
                      end: e.end,
                      color: e.color
                    }))}
                    eventClick={handleCalendarEventClick}
                    dateClick={handleCalendarDateClick}
                  />
                </div>
              </div>
            ) : (
              /* Rich Timeline List View */
              <div className="cht-space-y-6">
                {filteredEvents.length > 0 ? (
                  filteredEvents.map(event => (
                    <div
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className="cht-bg-white cht-p-6 sm:cht-p-8 cht-rounded-[32px] cht-border cht-border-gray-50 hover:cht-border-[#3e57da]/20 cht-shadow-sm hover:cht-shadow-xl hover:-cht-translate-y-1 transition-all cht-duration-500 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-6 cht-group cht-cursor-pointer"
                    >
                      <div className="cht-flex cht-items-start sm:cht-items-center cht-gap-6 cht-flex-1">
                        {/* Date Badge */}
                        <div className="cht-w-16 cht-h-16 cht-rounded-[18px] cht-bg-gray-50 group-hover:cht-bg-[#3e57da]/5 cht-flex cht-flex-col cht-items-center cht-justify-center cht-shrink-0 transition-colors">
                          <span className="cht-text-[9px] cht-font-black cht-text-gray-400 group-hover:cht-text-[#3e57da] cht-uppercase cht-tracking-widest">
                            {new Date(event.start).toLocaleString('en-US', { month: 'short' })}
                          </span>
                          <span className="cht-text-xl cht-font-black cht-text-black group-hover:cht-text-[#3e57da]">
                            {new Date(event.start).getDate()}
                          </span>
                        </div>

                        <div className="cht-space-y-2 cht-flex-1">
                          <div className="cht-flex cht-items-center cht-gap-2">
                            <span className={`cht-text-[9px] cht-font-black cht-px-2.5 cht-py-1.5 cht-rounded-xl cht-uppercase cht-tracking-widest ${event.colorClass} cht-text-white`}>
                              {event.type}
                            </span>
                            <span className={`cht-text-[9px] cht-font-bold cht-px-2.5 cht-py-0.5 cht-rounded-md ${event.status.includes('Starts') ? 'cht-bg-blue-50 cht-text-blue-600' : 'cht-bg-orange-50 cht-text-orange-600'}`}>
                              {event.status}
                            </span>
                          </div>
                          <h4 className="cht-text-lg sm:cht-text-xl cht-font-black cht-text-black group-hover:cht-text-[#3e57da] transition-colors">{event.title}</h4>
                          <p className="cht-text-xs sm:cht-text-sm cht-text-gray-400 cht-font-medium cht-leading-relaxed cht-line-clamp-2">{event.description}</p>
                        </div>
                      </div>

                      <div className="cht-flex sm:cht-flex-col cht-items-start sm:cht-items-end cht-gap-4 sm:cht-gap-2 cht-shrink-0 cht-pt-4 sm:cht-pt-0 border-t sm:border-t-0 border-gray-50">
                        <div className="cht-flex cht-items-center cht-gap-2 cht-text-[10px] cht-font-bold cht-text-gray-400 uppercase tracking-widest">
                          <Clock size={12} /> {event.time}
                        </div>
                        <div className="cht-flex cht-items-center cht-gap-2 cht-text-[10px] cht-font-bold cht-text-gray-400 uppercase tracking-widest">
                          <MapPin size={12} /> {event.location}
                        </div>
                        <div className="cht-mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="cht-bg-gray-50 group-hover:cht-bg-[#3e57da] group-hover:cht-text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEvent(event);
                            }}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="cht-bg-white cht-p-16 cht-rounded-[32px] cht-text-center cht-border cht-border-gray-50">
                    <CalendarIcon className="cht-mx-auto cht-text-gray-300 cht-mb-4" size={48} />
                    <h4 className="cht-text-base cht-font-black cht-text-black cht-uppercase cht-tracking-widest">No Events Found</h4>
                    <p className="cht-text-xs cht-text-gray-400 cht-font-medium cht-mt-1">Try resetting your search query or check back later.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Agenda */}
          <div className="lg:cht-col-span-4 cht-space-y-6">

            {/* Search */}
            <div className="cht-relative">
              <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search agenda or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="cht-w-full cht-bg-white cht-border cht-border-gray-100 cht-rounded-2xl cht-py-4 cht-pl-12 cht-pr-4 cht-text-sm cht-font-medium cht-shadow-sm cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
              />
            </div>

            {/* Upcoming Agenda */}
            <div className="cht-bg-white cht-p-8 cht-rounded-[32px] cht-border cht-border-gray-50 cht-shadow-sm cht-space-y-8">
              <div className="cht-flex cht-items-center cht-justify-between">
                <h4 className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-[0.2em]">Upcoming Agenda</h4>
                <div className="cht-p-2 cht-bg-gray-50 cht-rounded-lg cht-text-[#3e57da]"><CalendarIcon size={14} /></div>
              </div>

              <div className="cht-space-y-6">
                {filteredEvents.length > 0 ? (
                  filteredEvents.slice(0, 3).map((item) => (
                    <div
                      key={item.id}
                      onClick={() => setSelectedEvent(item)}
                      className="cht-flex cht-gap-4 cht-group cht-cursor-pointer"
                    >
                      <div className={`cht-w-1.5 cht-h-16 ${item.colorClass} cht-rounded-full cht-shrink-0 cht-transition-all group-hover:cht-scale-y-110`}></div>
                      <div className="cht-space-y-1.5 cht-flex-1">
                        <div className="cht-flex cht-items-center cht-justify-between">
                          <h6 className="cht-text-sm cht-font-black cht-text-black group-hover:cht-text-[#3e57da] transition-colors">{item.title}</h6>
                          <span className={`cht-text-[9px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded-md ${item.status.includes('Starts') ? 'cht-bg-blue-50 cht-text-blue-600' : 'cht-bg-orange-50 cht-text-orange-600'}`}>
                            {item.status}
                          </span>
                        </div>
                        <div className="cht-flex cht-items-center cht-gap-4 cht-text-[10px] cht-font-bold cht-text-gray-400 uppercase tracking-widest">
                          <span className="cht-flex cht-items-center cht-gap-1.5"><Clock size={12} /> {item.time}</span>
                          <span className="cht-flex cht-items-center cht-gap-1.5"><MapPin size={12} /> {item.location}</span>
                        </div>
                        <div className="cht-pt-1">
                          <span className="cht-text-[10px] cht-font-black cht-text-gray-300 cht-uppercase cht-tracking-widest">{item.date}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="cht-py-10 cht-text-center">
                    <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">No events found</p>
                  </div>
                )}
              </div>


            </div>

            {/* Quick Stats/Tip */}
            <div className="cht-bg-gradient-to-br cht-from-orange-500 cht-to-pink-500 cht-p-8 cht-rounded-[32px] cht-text-white cht-relative cht-overflow-hidden">
              <div className="cht-relative cht-z-10">
                <Clock size={32} className="cht-mb-4 cht-opacity-50" />
                <h5 className="cht-text-base cht-font-black cht-mb-1">Time Management Tip</h5>
                <p className="cht-text-[11px] cht-text-white/80 cht-leading-relaxed">Block out 90 minutes each morning for deep work on your projects. Consistency is the key to mastering complex stacks.</p>
              </div>
              <div className="cht-absolute -cht-right-6 -cht-bottom-6 cht-w-32 cht-h-32 cht-bg-white/10 cht-rounded-full cht-blur-2xl"></div>
            </div>
          </div>
        </div>
      </main>

      {/* Dynamic Glassmorphism Details Modal */}
      {selectedEvent && (
        <div className="cht-fixed cht-inset-0 cht-z-[2000] cht-flex cht-items-center cht-justify-center cht-overflow-y-auto cht-p-4">
          <div
            onClick={() => setSelectedEvent(null)}
            className="cht-absolute cht-inset-0 cht-h-full cht-w-full cht-bg-black/40 cht-backdrop-blur-md transition-opacity"
          ></div>

          <div className="cht-relative cht-bg-white/95 cht-backdrop-blur-2xl cht-w-full cht-max-w-xl cht-rounded-[32px] cht-p-8 sm:cht-p-10 cht-shadow-2xl cht-border cht-border-white/40 cht-space-y-6 cht-animate-in cht-zoom-in-95 cht-duration-300">
            <div className="cht-flex cht-items-start cht-justify-between">
              <div className="cht-space-y-2">
                <span className={`cht-text-[9px] cht-font-black cht-px-3 cht-py-1.5 cht-rounded-xl cht-uppercase cht-tracking-widest ${selectedEvent.colorClass} cht-text-white`}>
                  {selectedEvent.type}
                </span>
                <h3 className="cht-text-xl sm:cht-text-2xl cht-font-black cht-text-black cht-mt-2">{selectedEvent.title}</h3>
              </div>
              <button
                onClick={() => setSelectedEvent(null)}
                className="cht-p-2 cht-bg-gray-100 hover:cht-bg-gray-200 cht-rounded-full cht-transition-colors"
              >
                <svg className="cht-w-4 cht-h-4 cht-text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="cht-space-y-2">
              <h5 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">About the Event</h5>
              <p className="cht-text-xs sm:cht-text-sm cht-text-gray-600 cht-font-medium cht-leading-relaxed">{selectedEvent.description}</p>
            </div>

            <div className="cht-grid cht-grid-cols-2 cht-gap-4 cht-bg-gray-50/50 cht-p-4 cht-rounded-[20px] cht-border cht-border-gray-100">
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Time</span>
                <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-font-bold cht-text-black">
                  <Clock size={14} className="cht-text-[#3e57da]" /> {selectedEvent.time}
                </div>
              </div>
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Location</span>
                <div className="cht-flex cht-items-center cht-gap-2 cht-text-xs cht-font-bold cht-text-black">
                  <MapPin size={14} className="cht-text-[#3e57da]" /> {selectedEvent.location}
                </div>
              </div>
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Host / Instructor</span>
                <div className="cht-text-xs cht-font-bold cht-text-black">
                  {selectedEvent.host}
                </div>
              </div>
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Duration</span>
                <div className="cht-text-xs cht-font-bold cht-text-black">
                  {selectedEvent.duration}
                </div>
              </div>
            </div>

            <div className="cht-flex cht-flex-col sm:cht-flex-row cht-gap-4 cht-border-t cht-border-gray-100 cht-pt-6">
              {selectedEvent.meetingLink && (
                <Button
                  as="a"
                  href={selectedEvent.meetingLink}
                  target={selectedEvent.meetingLink.startsWith('http') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  variant="primary"
                  className="cht-flex-1"
                >
                  {selectedEvent.type === 'deadline' ? 'Submit Project' : 'Join Session'}
                </Button>
              )}
              {selectedEvent.type !== 'deadline' && (
                <Button
                  variant={selectedEvent.isRegistered ? "ghost" : "outline"}
                  className={`cht-flex-1 ${selectedEvent.isRegistered ? 'cht-text-green-600 cht-bg-green-50 hover:cht-bg-green-100' : ''}`}
                  onClick={() => handleRsvpToggle(selectedEvent)}
                >
                  {selectedEvent.isRegistered ? "✓ RSVP confirmed" : "RSVP for Event"}
                </Button>
              )}
              
              <div className="cht-relative cht-flex-1">
                <Button
                  variant="outline"
                  className="cht-w-full"
                  onClick={() => setIsCalendarMenuOpen(!isCalendarMenuOpen)}
                >
                  Add to Calendar
                </Button>
                
                {isCalendarMenuOpen && (
                  <div className="cht-absolute cht-bottom-full cht-left-0 cht-mb-2 cht-w-full cht-bg-white/95 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-rounded-2xl cht-shadow-xl cht-py-2 cht-z-20 cht-animate-in cht-fade-in cht-slide-in-from-bottom-2 cht-duration-200">
                    <a
                      href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(selectedEvent.title)}&details=${encodeURIComponent(selectedEvent.description)}&location=${encodeURIComponent(selectedEvent.location)}&dates=${formatToUTCBasic(selectedEvent.start)}/${formatToUTCBasic(selectedEvent.end)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cht-block cht-px-4 cht-py-2.5 cht-text-xs cht-font-black cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] transition-colors"
                      onClick={() => setIsCalendarMenuOpen(false)}
                    >
                      Google Calendar
                    </a>
                    <a
                      href={`https://outlook.office.com/calendar/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(selectedEvent.title)}&body=${encodeURIComponent(selectedEvent.description)}&location=${encodeURIComponent(selectedEvent.location)}&startdt=${formatToUTCBasic(selectedEvent.start)}&enddt=${formatToUTCBasic(selectedEvent.end)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cht-block cht-px-4 cht-py-2.5 cht-text-xs cht-font-black cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] transition-colors"
                      onClick={() => setIsCalendarMenuOpen(false)}
                    >
                      Outlook Calendar
                    </a>
                    <a
                      href={`https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(selectedEvent.title)}&desc=${encodeURIComponent(selectedEvent.description)}&in_loc=${encodeURIComponent(selectedEvent.location)}&st=${formatToUTCBasic(selectedEvent.start)}&et=${formatToUTCBasic(selectedEvent.end)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cht-block cht-px-4 cht-py-2.5 cht-text-xs cht-font-black cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] transition-colors"
                      onClick={() => setIsCalendarMenuOpen(false)}
                    >
                      Yahoo Calendar
                    </a>
                    <button
                      onClick={() => {
                        handleDownloadSingleEventICS(selectedEvent);
                        setIsCalendarMenuOpen(false);
                      }}
                      className="cht-w-full cht-text-left cht-block cht-px-4 cht-py-2.5 cht-text-xs cht-font-black cht-text-gray-700 hover:cht-bg-gray-50 hover:cht-text-[#3e57da] transition-colors"
                    >
                      Apple Calendar / iCal
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Schedule Date Events Modal */}
      {selectedDateEvents && (
        <div className="cht-fixed cht-inset-0 cht-z-[2000] cht-flex cht-items-center cht-justify-center cht-overflow-y-auto cht-p-4">
          <div
            onClick={() => setSelectedDateEvents(null)}
            className="cht-absolute cht-inset-0 cht-h-full cht-w-full cht-bg-black/40 cht-backdrop-blur-md transition-opacity"
          ></div>

          <div className="cht-relative cht-bg-white/95 cht-backdrop-blur-2xl cht-w-full cht-max-w-lg cht-rounded-[32px] cht-p-8 cht-shadow-2xl cht-border cht-border-white/40 cht-space-y-6 cht-animate-in cht-zoom-in-95 cht-duration-300">
            <div className="cht-flex cht-items-center cht-justify-between">
              <div className="cht-space-y-1">
                <span className="cht-text-[9px] cht-font-black cht-text-[#3e57da] cht-uppercase cht-tracking-widest">
                  Daily Schedule
                </span>
                <h3 className="cht-text-xl sm:cht-text-2xl cht-font-black cht-text-black">
                  {new Date(selectedDateEvents.dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </h3>
              </div>
              <button
                onClick={() => setSelectedDateEvents(null)}
                className="cht-p-2 cht-bg-gray-100 hover:cht-bg-gray-200 cht-rounded-full cht-transition-colors"
              >
                <svg className="cht-w-4 cht-h-4 cht-text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="cht-space-y-4">
              {selectedDateEvents.list.length > 0 ? (
                selectedDateEvents.list.map(event => (
                  <div
                    key={event.id}
                    onClick={() => {
                      setSelectedEvent(event);
                      setSelectedDateEvents(null);
                    }}
                    className="cht-p-4 cht-rounded-[20px] cht-border cht-border-gray-50 hover:cht-border-[#3e57da]/20 cht-bg-gray-50/50 hover:cht-bg-white cht-transition-all cht-duration-300 cht-cursor-pointer cht-flex cht-items-center cht-justify-between cht-group"
                  >
                    <div className="cht-space-y-1.5 cht-flex-1">
                      <div className="cht-flex cht-items-center cht-gap-2">
                        <span className={`cht-text-[8px] cht-font-black cht-px-2 cht-py-0.5 cht-rounded-md cht-uppercase cht-tracking-widest ${event.colorClass} cht-text-white`}>
                          {event.type}
                        </span>
                        <span className="cht-text-[9px] cht-font-bold cht-text-gray-400">
                          {event.time}
                        </span>
                      </div>
                      <h5 className="cht-text-sm sm:cht-text-base cht-font-black cht-text-black group-hover:cht-text-[#3e57da] transition-colors">
                        {event.title}
                      </h5>
                      <span className="cht-flex cht-items-center cht-gap-1 cht-text-[9px] cht-font-bold cht-text-gray-400 uppercase tracking-widest">
                        <MapPin size={10} /> {event.location}
                      </span>
                    </div>
                    <ChevronRight size={16} className="cht-text-gray-400 group-hover:cht-text-[#3e57da] transition-colors" />
                  </div>
                ))
              ) : (
                <div className="cht-py-12 cht-text-center cht-space-y-3">
                  <CalendarIcon className="cht-mx-auto cht-text-gray-300" size={40} />
                  <div>
                    <h5 className="cht-text-sm cht-font-black cht-text-black cht-uppercase cht-tracking-widest">No Events Scheduled</h5>
                    <p className="cht-text-xs cht-text-gray-400 cht-font-medium cht-mt-0.5">There are no lectures or deadlines on this day.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="cht-pt-2">
              <Button
                variant="ghost"
                className="cht-w-full cht-bg-gray-50 hover:cht-bg-gray-100"
                onClick={() => setSelectedDateEvents(null)}
              >
                Close View
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Events;
